import React from 'react'
import { ThemeProvider } from 'styled-components'

import Peracto from '@peracto/peracto'
import Config from '@peracto/peracto-config'
import PeractoUser from '@peracto/peracto-user'
import PeractoCatalogue from '@peracto/peracto-catalogue'
import PeractoCommon from '@peracto/peracto-common'
import PeractoLocations from '@peracto/peracto-locations'
import PeractoContent from '@peracto/peracto-content'
import PeractoPromotions from '@peracto/peracto-promotions'

import { UserEdit, UserForm, UserList } from './packages/User'
import { LocationsForm } from './packages/Locations'
import { PromotionsForm, PromotionsList } from './packages/Promotions'
import Integrations from './packages/Integrations'
import LastImportedFiles from './packages/LastImportedFiles'
import NavigationExports from './packages/NavigationExports'
import ProductExports from './packages/ProductExports'
import LandingPagesData from './packages/LandingPagesData'
import PromotionsData from './packages/PromotionsData'
import UserExports from './packages/UserExports'
import UserManageData from './packages/UserManageData'
import OrderRevenueExport from './packages/OrderRevenueExport'
import {
    ShippingServiceForm,
    ShippingServiceList,
    OrdersList,
    OrdersView,
    CategoryForm,
} from './packages/Catalogue'

import AttributeImport from '@peracto/peracto-catalogue/dist/components/Attributes/AttributeImport'
import ProductImport from '@peracto/peracto-catalogue/dist/components/Products/ProductImport'
import LocationsImport from '@peracto/peracto-locations/dist/components/LocationsImport'
import RedirectsImport from '@peracto/peracto-common/dist/components/Redirects/RedirectsImport'
import { withRoleGuard } from './components/RoleGuard'
import {
    PRODUCT_IMPORT_ROLES,
    ATTRIBUTE_IMPORT_ROLES,
    LOCATIONS_IMPORT_ROLES,
    REDIRECTS_IMPORT_ROLES,
} from './constants/roles'

import { AdminTheme, GlobalStyles, EditorTheme } from './theme'
import menu from './menu'
import blocks from './blocks'

const config = new Config({
    api: process.env.REACT_APP_API,
    algolia: {
        applicationId: process.env.REACT_APP_ALGOLIA_APPLICATION_ID,
        searchApiKey: process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY,
        indexName: process.env.REACT_APP_ALGOLIA_INDEX_NAME,
    },
    modules: [
        Integrations(),
        LastImportedFiles(),
        NavigationExports(),
        ProductExports(),
        LandingPagesData(),
        PromotionsData(),
        UserExports(),
        OrderRevenueExport(),
        PeractoLocations({ LocationsForm, LocationsImport: withRoleGuard(LocationsImport, LOCATIONS_IMPORT_ROLES) }),
        UserManageData(),
        PeractoUser({ UserEdit, UserForm, UserList }),
        PeractoCatalogue({
            ShippingServiceForm,
            ShippingServiceList,
            OrdersList,
            OrdersView,
            CategoryForm,
            ProductImport: withRoleGuard(ProductImport, PRODUCT_IMPORT_ROLES),
            AttributeImport: withRoleGuard(AttributeImport, ATTRIBUTE_IMPORT_ROLES),
        }),
        PeractoCommon({ RedirectsImport: withRoleGuard(RedirectsImport, REDIRECTS_IMPORT_ROLES) }),
        PeractoContent(),
        PeractoPromotions({ PromotionsForm, PromotionsList }),
    ],
    editorConfig: {
        isTailwind: true,
        theme: EditorTheme,
        colorOptions: [
            { label: 'Brand Blue', value: '#003D73' },
            { label: 'Brand Red', value: '#E30613' },
            { label: 'Brand Yellow', value: '#FFED00' },
            { label: 'Brand Green', value: '#098D45' },
            { label: 'PDP Banner Red', value: '#B91C1C' },
        ],
        buttonColorOptions: [
            { label: 'Brand Blue', value: 'secondary' },
            { label: 'Brand Red', value: 'primary' },
            { label: 'Brand Yellow', value: 'tertiary_button' },
            { label: 'Brand Green', value: 'basket' },
            { label: 'PDP Banner Red', value: 'pdp_banner_red' },
        ],
        locales: ['en_GB'],
        blocks,
        inputTypes: {},
        globalStyles: GlobalStyles,
    },
    menu,
    features: {
        products: {
            bulkPricing: true,
            showPricesAsCurrency: true,
            hideClearancePrice: true,
            hideSalePrice: true,
            showRRPAsWasPrice: true,
            productVariants: true,
            maxQuantity: true,
        },
        shippingServices: {
            deliveryDays: true,
        },
        promotions: {
            orderTransferCode: true,
        },
        user: {
            userGroups: true,
        },
    },
})

const App = () => (
    <ThemeProvider theme={AdminTheme}>
        <Peracto config={config} />
    </ThemeProvider>
)

export default App
